import { useEffect, useState } from 'react';
import type { Account } from '../lib/accounts';
import { createClient, updateClient, newClientId } from '../lib/clientStore';

interface Props {
  mode: 'create' | 'edit';
  initial?: Account;
  onClose: () => void;
  onSaved: (a: Account) => void;
}

const STATUSES: Account['status'][] = ['active', 'prospect', 'inactive'];

export default function ClientEditor({ mode, initial, onClose, onSaved }: Props) {
  const [f, setF] = useState(() => ({
    name: initial?.name ?? '',
    industry: initial?.industry ?? '',
    source: initial?.source ?? 'crm',
    status: (initial?.status ?? 'active') as Account['status'],
    website: initial?.website ?? '',
    phone: initial?.phone ?? '',
    fax: initial?.fax ?? '',
    rating: initial?.rating ?? '',
    healthScore: initial?.healthScore != null ? String(initial.healthScore) : '',
    contactName: initial?.contactName ?? '',
    email: initial?.email ?? '',
    bStreet: initial?.billing?.street ?? '', bCity: initial?.billing?.city ?? '', bState: initial?.billing?.state ?? '', bPostal: initial?.billing?.postal ?? '', bCountry: initial?.billing?.country ?? '',
    sStreet: initial?.shipping?.street ?? '', sCity: initial?.shipping?.city ?? '', sState: initial?.shipping?.state ?? '', sPostal: initial?.shipping?.postal ?? '', sCountry: initial?.shipping?.country ?? '',
    tags: (initial?.tags ?? []).join(', '),
    notes: initial?.notes ?? '',
  }));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));

  const build = (): Account => ({
    id: initial?.id ?? newClientId(),
    name: f.name.trim() || 'Untitled client',
    type: initial?.type ?? 'client',
    status: f.status,
    industry: f.industry.trim() || undefined,
    source: f.source.trim() || undefined,
    website: f.website.trim() || undefined,
    phone: f.phone.trim() || undefined,
    fax: f.fax.trim() || undefined,
    rating: f.rating.trim() || undefined,
    recordId: initial?.recordId,
    contactName: f.contactName.trim() || undefined,
    email: f.email.trim() || undefined,
    healthScore: f.healthScore.trim() ? Math.max(0, Math.min(100, Math.round(Number(f.healthScore) || 0))) : undefined,
    billing: { street: f.bStreet.trim() || undefined, city: f.bCity.trim() || undefined, state: f.bState.trim() || undefined, postal: f.bPostal.trim() || undefined, country: f.bCountry.trim() || undefined },
    shipping: { street: f.sStreet.trim() || undefined, city: f.sCity.trim() || undefined, state: f.sState.trim() || undefined, postal: f.sPostal.trim() || undefined, country: f.sCountry.trim() || undefined },
    tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
    notes: f.notes.trim() || undefined,
    _origin: initial?._origin ?? 'customers',
  });

  const save = async () => {
    if (!f.name.trim()) { setMsg('Client name is required.'); return; }
    setSaving(true); setMsg(null);
    const a = build();
    const r = mode === 'create' ? await createClient(a) : await updateClient(a.id, a, a);
    setSaving(false);
    if (r.ok) { onSaved(a); return; }
    // Saved to the local overlay; live sync failed (offline/demo/permissions).
    setMsg('Saved locally — live sync needs a signed-in account with edit access. ' + (r.error ? `(${r.error})` : ''));
  };

  const F = ({ label, k, ph, type = 'text' }: { label: string; k: keyof typeof f; ph?: string; type?: string }) => (
    <label className="ed-field">
      <span>{label}</span>
      <input type={type} value={f[k] as string} placeholder={ph} onChange={(e) => set(k, e.target.value)} />
    </label>
  );

  return (
    <div className="ed-scrim" onClick={onClose}>
      <div className="ed-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ed-head">
          <div>
            <div className="ed-eyebrow">{mode === 'create' ? 'New client' : 'Edit client'}</div>
            <h3>{mode === 'create' ? 'Create client profile' : f.name || 'Edit profile'}</h3>
          </div>
          <button className="ed-x" onClick={onClose}>✕</button>
        </div>

        <div className="ed-body">
          <div className="ed-section">Identity</div>
          <div className="ed-grid">
            <label className="ed-field span2"><span>Client name *</span><input value={f.name} placeholder="Acme Foods, Inc." onChange={(e) => set('name', e.target.value)} /></label>
            <F label="Industry" k="industry" ph="Food & Beverage" />
            <label className="ed-field"><span>Status</span>
              <select value={f.status} onChange={(e) => set('status', e.target.value)}>
                {STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>
            </label>
            <F label="Source" k="source" ph="Referral / Web / OS" />
            <F label="Rating" k="rating" ph="A / B / C" />
            <F label="Health score (0–100)" k="healthScore" ph="82" type="number" />
            <F label="Website" k="website" ph="https://" />
          </div>

          <div className="ed-section">Primary contact</div>
          <div className="ed-grid">
            <F label="Contact name" k="contactName" ph="Jordan Lee" />
            <F label="Email" k="email" ph="jordan@acme.com" type="email" />
            <F label="Phone" k="phone" ph="(555) 555-1234" />
            <F label="Fax" k="fax" />
          </div>

          <div className="ed-section">Billing address</div>
          <div className="ed-grid">
            <label className="ed-field span2"><span>Street</span><input value={f.bStreet} onChange={(e) => set('bStreet', e.target.value)} /></label>
            <F label="City" k="bCity" />
            <F label="State" k="bState" />
            <F label="Postal code" k="bPostal" />
            <F label="Country" k="bCountry" />
          </div>

          <div className="ed-section">Shipping address</div>
          <div className="ed-grid">
            <label className="ed-field span2"><span>Street</span><input value={f.sStreet} onChange={(e) => set('sStreet', e.target.value)} /></label>
            <F label="City" k="sCity" />
            <F label="State" k="sState" />
            <F label="Postal code" k="sPostal" />
            <F label="Country" k="sCountry" />
          </div>

          <div className="ed-section">More</div>
          <div className="ed-grid">
            <label className="ed-field span2"><span>Tags (comma separated)</span><input value={f.tags} placeholder="key account, reorder" onChange={(e) => set('tags', e.target.value)} /></label>
            <label className="ed-field span2"><span>Notes</span><textarea rows={3} value={f.notes} onChange={(e) => set('notes', e.target.value)} /></label>
          </div>

          {msg && <div className="ed-msg">{msg}</div>}
        </div>

        <div className="ed-foot">
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : mode === 'create' ? 'Create client' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}
